import { useState } from 'react';
import { X } from 'lucide-react';
import beeIcon from '../../assets/icons/assistant-bee.png';
import owlIcon from '../../assets/icons/assistant-owl.png';
import squirrelIcon from '../../assets/icons/assistant-squirrel.png';
import UserAvatar from '../common/UserAvatar';
import { User } from '../../services/userService';

interface HistoryEntry {
  id: string;
  summary: string;
  role: 'user' | 'assistant';
  model?: 'norm' | 'exp' | 'eco';
  timestamp: string;
  generated?: boolean; // True for auto-generated placeholders (like "[Edited code]"), false for meaningful descriptions
  isActive?: boolean; // True if this is the currently active edit
  onCurrentPath?: boolean; // True if this edit is on the current path (not branched off)
  isFutureEdit?: boolean; // True if this edit is ahead of the current position (future edit)
}

interface EditHistoryProps {
  history: HistoryEntry[];
  onRestoreVersion?: (versionId: string) => void;
  currentUser?: User | null;
  isLoading?: boolean;
  showBranchedEdits?: boolean;
}

const MODEL_INFO = {
  norm: { name: 'Bee', icon: beeIcon },
  exp: { name: 'Owl', icon: owlIcon },
  eco: { name: 'Squirrel', icon: squirrelIcon }
};

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

export default function EditHistory({ history, onRestoreVersion, currentUser, isLoading = false, showBranchedEdits = true }: EditHistoryProps) {
  const [confirmingRestore, setConfirmingRestore] = useState<string | null>(null);

  // Filter history based on showBranchedEdits toggle
  const filteredHistory = showBranchedEdits 
    ? history 
    : history.filter(entry => {
        // Show if: on current path, is future edit, or is active
        const isBranched = entry.onCurrentPath === false && entry.isFutureEdit !== true;
        return !isBranched;
      });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-600 dark:text-dark-text-muted p-6">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mb-2"></div>
        <p className="text-sm">Loading edit history...</p>
      </div>
    );
  }

  if (filteredHistory.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-600 dark:text-dark-text-muted p-6">
        <p className="text-sm">No edits yet</p>
        <p className="text-xs mt-1 text-gray-500 dark:text-dark-text-muted">Your edit history will appear here</p>
      </div>
    );
  }


  const handleRestoreClick = (entryId: string) => {
    setConfirmingRestore(entryId);
  };

  const handleConfirmRestore = () => {
    if (confirmingRestore && onRestoreVersion) {
      onRestoreVersion(confirmingRestore);
    }
    setConfirmingRestore(null);
  };

  const handleCancelRestore = () => {
    setConfirmingRestore(null);
  };

  return (
    <>
      <div className="p-10 space-y-4">
        {filteredHistory.map((entry, index) => {
          const isLast = index === filteredHistory.length - 1;
          const isActive = entry.isActive || false; // Check the isActive field from backend
          const isClickable = !isActive && onRestoreVersion;
          const modelInfo = entry.model ? MODEL_INFO[entry.model] : null;
          const isBranched = entry.onCurrentPath === false && entry.isFutureEdit !== true;
          
          return (
            <div key={entry.id} className="relative pl-8">
              {/* Timeline dot */}
              <div className={`absolute left-0 top-1 w-5 h-5 rounded-full border-2 bg-white dark:bg-gray-800 z-10 flex items-center justify-center ${
                entry.isFutureEdit ? 'border-gray-400 dark:border-gray-400 opacity-40' : 
                isBranched ? 'border-red-500 opacity-50' : 'border-gray-400 dark:border-gray-400'
              }`}>
                {/* X icon for edits truly branched off (not on current path AND not future) */}
                {isBranched && (
                  <X className="w-4 h-4 text-red-500 stroke-[2.5]" strokeWidth={2.5} />
                )}
                {/* Filled inner circle for active edit */}
                {isActive && (
                  <div className="w-2.5 h-2.5 rounded-full bg-gray-600 dark:bg-gray-400" />
                )}
              </div>
              
              {/* Timeline line */}
              {!isLast && (
                <div className="absolute left-[9px] top-6 w-0.5 h-full bg-gray-300 dark:bg-gray-600" />
              )}
              
              {/* Content */}
              <div 
                className={`pb-6 ${isClickable ? 'cursor-pointer group' : ''} ${
                  entry.isFutureEdit ? 'opacity-40' : isBranched ? 'opacity-50' : ''
                }`}
                onClick={isClickable ? () => handleRestoreClick(entry.id) : undefined}
              >
                <p className={`text-sm leading-relaxed mb-2 ${
                  isClickable ? 'group-hover:underline' : ''
                } ${
                  entry.generated === true ? 'text-gray-500 dark:text-dark-text-muted' : 'text-gray-700 dark:text-dark-text'
                }`}>
                  {entry.summary}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {entry.role === 'assistant' && modelInfo ? (
                      <>
                        <img 
                          src={modelInfo.icon} 
                          alt={modelInfo.name} 
                          className="w-4 h-4 rounded-full object-contain" 
                        />
                        <span className="text-xs text-gray-600 dark:text-dark-text-muted">{modelInfo.name}</span>
                      </>
                    ) : (
                      <>
                        <UserAvatar currentUser={currentUser} size="xs" />
                        <span className="text-xs text-gray-600 dark:text-dark-text-muted">You</span>
                      </>
                    )}
                  </div>
                  
                  <span className="text-xs text-gray-500 dark:text-dark-text-muted">
                    {formatTime(entry.timestamp)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirmation Dialog */}
      {confirmingRestore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4 border border-gray-200 dark:border-dark-border">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">
              Move back to this edit?
            </h3>
            <p className="text-sm text-gray-600 dark:text-dark-text-muted mb-6">
              This will restore the code to this version.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelRestore}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-muted bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRestore}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

